// @mui
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

/**
 * Creates a blurred background image with a solid color overlay.
 * @param {Object} props
 * @param {string} [props.color] - The color of the background. Defaults to #000000.
 * @param {number} [props.blur] - The blur radius in pixels. Defaults to 6.
 * @param {number} [props.opacity] - The opacity of the color overlay. Defaults to 0.8.
 * @param {string} [props.imgUrl] - The URL of the background image. If not provided, the function returns a style object with just the color and blur.
 * @returns {Object} - A style object with the blurred background image and color overlay.
 */
export function bgBlur(props) {
  const color = props?.color || '#000000';
  const blur = props?.blur || 6;
  const opacity = props?.opacity || 0.8;
  const imgUrl = props?.imgUrl;

  if (imgUrl) {
    return {
      position: 'relative',
      backgroundImage: `url(${imgUrl})`,
      '&:before': {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 9,
        content: '""',
        width: '100%',
        height: '100%',
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        backgroundColor: alpha(color, opacity),
      },
    };
  }

  return {
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    backgroundColor: alpha(color, opacity),
  };
}

// ----------------------------------------------------------------------

/**
 * Generates a style object with a gradient background. If an image URL is provided, the gradient is overlayed on top of the image.
 * @param {Object} props - The options for the gradient.
 * @param {string} [props.direction] - The direction of the gradient. Defaults to 'to bottom'.
 * @param {string} [props.startColor] - The starting color of the gradient. If not provided, the function will use the color prop.
 * @param {string} [props.endColor] - The ending color of the gradient. If not provided, the function will use the color prop.
 * @param {string} [props.imgUrl] - The URL of the background image. If provided, the gradient will be overlayed on top of the image.
 * @param {string} [props.color] - The color of the gradient. If not provided, the function will default to #000000.
 * @returns {Object} - A style object with the gradient background.
 */
export function bgGradient(props) {
  const direction = props?.direction || 'to bottom';
  const startColor = props?.startColor;
  const endColor = props?.endColor;
  const imgUrl = props?.imgUrl;
  const color = props?.color;

  if (imgUrl) {
    return {
      background: `linear-gradient(${direction}, ${startColor || color}, ${endColor || color}), url(${imgUrl})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    };
  }

  return {
    background: `linear-gradient(${direction}, ${startColor}, ${endColor})`,
  };
}

// ----------------------------------------------------------------------

/**
 * Generates a style object that applies a gradient to the text of an element.
 * @param {string} value - The value of the gradient, in the format accepted by the background CSS property.
 * @returns {Object} - A style object with the gradient applied to the text.
 */
export function textGradient(value) {
  return {
    background: `-webkit-linear-gradient(${value})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };
}

// ----------------------------------------------------------------------

/**
 * Generates a style object that applies a filter to an element.
 * @param {string} value - The value of the filter, in the format accepted by the filter CSS property.
 * @returns {Object} - A style object with the filter applied.
 */
export function filterStyles(value) {
  return {
    filter: value,
    WebkitFilter: value,
    MozFilter: value,
  };
}

// ----------------------------------------------------------------------

export const hideScrollbarY = {
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

// ----------------------------------------------------------------------

export const hideScrollbarX = {
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  overflowX: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};
